import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { User, CreateUserRequest, UpdateUserRequest, UserFilters, UserRoleType } from '../interfaces/User';
import { userService } from '../services/userService';

interface UserState {
  users: User[];
  loading: boolean;
  error: string | null;
  filters: UserFilters;
  currentPage: number;
  totalPages: number;
  totalUsers: number;
}

type UserAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USERS'; payload: { users: User[]; total: number; page: number; limit: number } }
  | { type: 'ADD_USER'; payload: User }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'DELETE_USER'; payload: number }
  | { type: 'SET_FILTERS'; payload: Partial<UserFilters> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_PAGE'; payload: number };

const initialState: UserState = {
  users: [],
  loading: false,
  error: null,
  filters: {
    role: 'all',
    status: 'all',
    search: '',
  },
  currentPage: 1,
  totalPages: 1,
  totalUsers: 0,
};

const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_USERS':
      return { 
        ...state, 
        users: action.payload.users,
        totalUsers: action.payload.total,
        currentPage: action.payload.page,
        totalPages: Math.ceil(action.payload.total / action.payload.limit)
      };
    case 'ADD_USER':
      return { 
        ...state, 
        users: [...state.users, action.payload],
        totalUsers: state.totalUsers + 1
      };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.id ? action.payload : user
        ),
      };
    case 'DELETE_USER':
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.payload),
        totalUsers: state.totalUsers - 1
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        currentPage: 1,
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: {
          role: 'all',
          status: 'all',
          search: '',
        },
        currentPage: 1,
      };
    case 'SET_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
    default:
      return state;
  }
};

interface UserContextValue {
  state: UserState;
  actions: {
    loadUsers: (page?: number, limit?: number) => Promise<void>;
    createUser: (user: CreateUserRequest) => Promise<void>;
    updateUser: (id: number, user: UpdateUserRequest) => Promise<void>;
    deleteUser: (id: number) => Promise<void>;
    activateUser: (id: number) => Promise<void>;
    deactivateUser: (id: number) => Promise<void>;
    resetUserPassword: (id: number) => Promise<{ temporary_password: string }>;
    bulkActivate: (userIds: number[]) => Promise<void>;
    bulkDeactivate: (userIds: number[]) => Promise<void>;
    bulkChangeRole: (userIds: number[], newRole: UserRoleType) => Promise<void>;
    setFilters: (filters: Partial<UserFilters>) => void;
    resetFilters: () => void;
    setPage: (page: number) => void;
    getFilteredUsers: () => User[];
    getUserStats: () => {
      total: number;
      active: number;
      inactive: number;
      admins: number;
      gestors: number;
      emissors: number;
      readers: number;
    };
    validateEmail: (email: string) => Promise<boolean>;
    validateUsername: (username: string) => Promise<boolean>;
  };
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  const actions: UserContextValue['actions'] = {
    loadUsers: async (page: number = 1, limit: number = 50) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        const response = await userService.getUsers(page, limit);
        dispatch({ type: 'SET_USERS', payload: response });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar usuários' });
        console.error('Erro ao carregar usuários:', error);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },

    createUser: async (user: CreateUserRequest) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const newUser = await userService.createUser(user);
        dispatch({ type: 'ADD_USER', payload: newUser });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao criar usuário' });
        console.error('Erro ao criar usuário:', error);
        throw error;
      }
    },

    updateUser: async (id: number, user: UpdateUserRequest) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const updatedUser = await userService.updateUser(id, user);
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao atualizar usuário' });
        console.error('Erro ao atualizar usuário:', error);
        throw error;
      }
    },

    deleteUser: async (id: number) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        await userService.deleteUser(id);
        dispatch({ type: 'DELETE_USER', payload: id });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao excluir usuário' });
        console.error('Erro ao excluir usuário:', error);
        throw error;
      }
    },

    activateUser: async (id: number) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const updatedUser = await userService.activateUser(id);
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao ativar usuário' });
        throw error;
      }
    },

    deactivateUser: async (id: number) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const updatedUser = await userService.deactivateUser(id);
        dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao desativar usuário' });
        throw error;
      }
    },

    resetUserPassword: async (id: number) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        const result = await userService.resetUserPassword(id);
        return result;
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro ao resetar senha' });
        throw error;
      }
    },

    bulkActivate: async (userIds: number[]) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        await userService.bulkAction({
          user_ids: userIds,
          action: 'activate'
        });
        await actions.loadUsers(state.currentPage);
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro na ativação em lote' });
        throw error;
      }
    },

    bulkDeactivate: async (userIds: number[]) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        await userService.bulkAction({
          user_ids: userIds,
          action: 'deactivate'
        });
        await actions.loadUsers(state.currentPage);
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro na desativação em lote' });
        throw error;
      }
    },

    bulkChangeRole: async (userIds: number[], newRole: UserRoleType) => {
      try {
        dispatch({ type: 'SET_ERROR', payload: null });
        await userService.bulkAction({
          user_ids: userIds,
          action: 'change_role',
          new_role: newRole
        });
        await actions.loadUsers(state.currentPage);
      } catch (error: any) {
        dispatch({ type: 'SET_ERROR', payload: 'Erro na alteração de role em lote' });
        throw error;
      }
    },

    setFilters: (filters: Partial<UserFilters>) => {
      dispatch({ type: 'SET_FILTERS', payload: filters });
    },

    resetFilters: () => {
      dispatch({ type: 'RESET_FILTERS' });
    },

    setPage: (page: number) => {
      dispatch({ type: 'SET_PAGE', payload: page });
    },

    getFilteredUsers: () => {
      if (!Array.isArray(state.users)) {
        return [];
      }
      
      let filtered = [...state.users];

      if (state.filters.status !== 'all') {
        filtered = filtered.filter(user =>
          state.filters.status === 'active' ? user.is_active : !user.is_active
        );
      }

      if (state.filters.role !== 'all') {
        filtered = filtered.filter(user => user.role === state.filters.role);
      }

      if (state.filters.search) {
        const searchLower = state.filters.search.toLowerCase();
        filtered = filtered.filter(user =>
          user.username.toLowerCase().includes(searchLower) ||
          user.lastname.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.created_by.toLowerCase().includes(searchLower)
        );
      }

      return filtered;
    },

    getUserStats: () => {
      const users = Array.isArray(state.users) ? state.users : [];
      return {
        total: users.length,
        active: users.filter(u => u.is_active).length,
        inactive: users.filter(u => !u.is_active).length,
        admins: users.filter(u => u.role === 'admin').length,
        gestors: users.filter(u => u.role === 'gestor').length,
        emissors: users.filter(u => u.role === 'emissor').length,
        readers: users.filter(u => u.role === 'reader').length,
      };
    },

    validateEmail: async (email: string) => {
      try {
        const result = await userService.validateEmail(email);
        return result.available;
      } catch (error) {
        return false;
      }
    },

    validateUsername: async (username: string) => {
      try {
        const result = await userService.validateUsername(username);
        return result.available;
      } catch (error) {
        return false;
      }
    },
  };

  return (
    <UserContext.Provider value={{ state, actions }}>
      {children}
    </UserContext.Provider>
  );
};